import { AlertCircle, ArrowRight, Mail, Phone } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { resolveFlexSiteUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export interface ErrorUIProps {
  title: string;
  description: string;
  error?: Error;
  showStackTrace?: boolean;
  onBack?: () => void;
  onHome?: () => void;
}

export function ErrorUI({
  title,
  description,
  error,
  showStackTrace = false,
  onHome,
}: ErrorUIProps) {
  const { t } = useTranslation();
  const { lang } = useParams();
  const handleHome =
    onHome ?? (() => (window.location.href = resolveFlexSiteUrl("/", lang)));

  return (
    <div className="min-h-screen bg-matte-yellow-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="shadow-lg">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto bg-destructive/10 w-12 h-12 rounded-full flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <div className="space-y-2">
              <CardTitle>{title}</CardTitle>
              <CardDescription className="text-base">
                {description}
              </CardDescription>
            </div>
          </CardHeader>

          {showStackTrace &&
            error?.stack &&
            process.env.NODE_ENV === "development" && (
              <CardContent>
                <Alert variant="destructive">
                  <AlertTitle>Stack Trace</AlertTitle>
                  <AlertDescription className="mt-2 font-mono text-xs overflow-x-auto">
                    <pre>{error.stack}</pre>
                  </AlertDescription>
                </Alert>
              </CardContent>
            )}

          <Separator />

          <CardContent className="space-y-4">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-center">
                {t("errorUi.customerService")}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-4">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div className="space-y-0.5">
                    <p className="text-sm text-muted-foreground">
                      {t("errorUi.emailUs")}
                    </p>
                    <a
                      href={`mailto:${t("errorUi.email")}`}
                      className="text-sm font-medium hover:underline"
                    >
                      {t("errorUi.email")}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-4">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div className="space-y-0.5">
                    <p className="text-sm text-muted-foreground">
                      {t("errorUi.phoneSupport")}
                    </p>
                    <p className="text-sm font-medium">{t("errorUi.phone")}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("errorUi.phoneHours")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex gap-2 justify-center">
            <Button variant="secondary" onClick={handleHome}>
              {t("errorUi.home")}
              <ArrowRight className="mr-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
